import React from 'react';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

interface SubItemRowProps {
  icon: IconName;
  title: string;
  subtitle: string;
  /** Renderiza o título em fonte mono (headers, nomes técnicos). */
  mono?: boolean;
  /** Renderiza o subtítulo em fonte mono (valor de header). */
  monoSub?: boolean;
  onEdit: () => void;
  onRemove: () => void;
  testId: string;
}

/**
 * Linha de exibição de um sub-item configurado (header, parâmetro, filtro).
 * Ícone + nome + descrição + botões de editar/remover. A edição em si acontece
 * no `SubItemModal` — esta linha é apenas leitura + disparo das ações.
 */
export const SubItemRow: React.FC<SubItemRowProps> = ({
  icon,
  title,
  subtitle,
  mono = false,
  monoSub = false,
  onEdit,
  onRemove,
  testId,
}) => {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 rounded-lg border p-3"
      style={{ borderColor: 'var(--theme-card-border)' }}
    >
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-lg"
        style={{
          width: 36,
          height: 36,
          backgroundColor: 'var(--theme-surface-sunken)',
          color: 'var(--theme-muted-text)',
        }}
      >
        <Icon name={icon} className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div
          className={`truncate text-sm font-medium ${mono ? 'font-mono' : ''}`}
          style={{ color: 'var(--theme-card-text)' }}
        >
          {title}
        </div>
        <div
          className={`truncate text-xs ${monoSub ? 'font-mono' : ''}`}
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {subtitle}
        </div>
      </div>

      <button
        type="button"
        onClick={onEdit}
        data-testid={`${testId}-edit`}
        className="p-2 rounded-lg transition-colors flex-shrink-0"
        style={{ color: 'var(--theme-muted-text)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label="Editar"
      >
        <Icon name="edit" className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={onRemove}
        data-testid={`${testId}-remove`}
        className="p-2 rounded-lg transition-colors flex-shrink-0"
        style={{ color: 'var(--theme-error-color)' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-error-background)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label="Remover"
      >
        <Icon name="trash" className="w-4 h-4" />
      </button>
    </div>
  );
};

SubItemRow.displayName = 'SubItemRow';
